<script setup>
import { ref, computed, watch, onMounted, onUnmounted } from 'vue';
import { sb, authFetch } from '../../composables/useSupabase.js';
import { SUPABASE_URL, SUPABASE_ANON_KEY } from '../../composables/useSupabase.js';

async function sbRpc(fn, params = {}) {
  const res = await authFetch(`${SUPABASE_URL}/rest/v1/rpc/${fn}`, {
    method: 'POST',
    body: JSON.stringify(params),
  });
  if (!res.ok) return null;
  const text = await res.text();
  return text ? JSON.parse(text) : null;
}

const props = defineProps({
  chat: { type: Object, default: () => ({}) },
});

// ── State ──
const autopilotActive = ref(false);
const autopilotLoading = ref(false);
const instructionInput = ref('');
const showInstruction = ref(false);
const convId = ref(null);
const loading = ref(true);
const openMenu = ref(false);
const pillRef = ref(null);

// ── Phone from conversation sender ──
const phone = computed(() => {
  const sender = props.chat?.meta?.sender;
  if (!sender) return '';
  const raw = sender.phone_number || sender.identifier || '';
  return raw.replace(/^\+/, '').replace(/\D/g, '');
});

// ── Load functions ──
async function loadConversation() {
  if (!phone.value) { loading.value = false; return; }
  const rows = await sb(
    `wa_conversations?phone=eq.${phone.value}&select=id&limit=1`
  );
  if (rows && rows[0]) {
    convId.value = rows[0].id;
  }
  // Load autopilot status from session_vars
  const apRows = await sb(
    `session_vars?phone=eq.${phone.value}&variable=eq.autopilot_active&select=value&limit=1`
  );
  // Default is ON — autopilot is active unless explicitly set to 'false'
  const apValue = apRows?.[0]?.value;
  autopilotActive.value = apValue !== 'false';
  loading.value = false;
}

// ── Autopilot actions ──
async function deactivateAutopilot() {
  if (!phone.value) return;
  autopilotLoading.value = true;
  try {
    await sbRpc('toggle_autopilot', { p_phone: phone.value, p_active: false });
    autopilotActive.value = false;
  } finally {
    autopilotLoading.value = false;
    openMenu.value = false;
  }
}

async function activatePassive() {
  if (!convId.value) return;
  autopilotLoading.value = true;
  try {
    await sb('wa_commands', {
      method: 'POST',
      body: {
        conversation_id: convId.value,
        command: 'activateAutopilot',
        payload: { proactive: false },
        status: 'pending',
      },
    });
    autopilotActive.value = true;
  } finally {
    autopilotLoading.value = false;
    openMenu.value = false;
  }
}

async function activateProactive(instruction) {
  if (!convId.value) return;
  autopilotLoading.value = true;
  try {
    const payload = { proactive: true };
    if (instruction) payload.instruction = instruction;
    await sb('wa_commands', {
      method: 'POST',
      body: {
        conversation_id: convId.value,
        command: 'activateAutopilot',
        payload,
        status: 'pending',
      },
    });
    autopilotActive.value = true;
  } finally {
    autopilotLoading.value = false;
    openMenu.value = false;
    showInstruction.value = false;
    instructionInput.value = '';
  }
}

function toggleMenu() {
  if (openMenu.value) {
    openMenu.value = false;
    showInstruction.value = false;
    instructionInput.value = '';
  } else {
    openMenu.value = true;
    showInstruction.value = false;
    instructionInput.value = '';
  }
}

// ── Click outside handler ──
function onClickOutside(e) {
  if (pillRef.value && !pillRef.value.contains(e.target)) {
    openMenu.value = false;
    showInstruction.value = false;
    instructionInput.value = '';
  }
}

// ── Lifecycle ──
watch(
  () => props.chat?.id,
  async () => {
    loading.value = true;
    convId.value = null;
    autopilotActive.value = false;
    openMenu.value = false;
    showInstruction.value = false;
    instructionInput.value = '';
    await loadConversation();
  }
);

onMounted(async () => {
  document.addEventListener('click', onClickOutside);
  await loadConversation();
});

onUnmounted(() => {
  document.removeEventListener('click', onClickOutside);
});
</script>

<template>
  <div v-if="!loading" ref="pillRef" class="hw-pill-wrap">
    <button
      class="hw-pill hw-autopilot"
      :class="{ active: autopilotActive }"
      :title="autopilotActive ? 'Autopilot activo' : 'Autopilot apagado'"
      :disabled="autopilotLoading"
      @click.stop="toggleMenu"
    >
      <span class="hw-ap-dot" :class="{ on: autopilotActive }" />
      <span>{{ autopilotActive ? 'Bot ON' : 'Bot OFF' }}</span>
      <svg class="hw-chevron" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="6 9 12 15 18 9"/></svg>
    </button>
    <!-- Autopilot dropdown -->
    <div v-if="openMenu" class="hw-dropdown hw-ap-dropdown" @click.stop>
      <div class="hw-ap-header">
        <span class="hw-ap-dot" :class="{ on: autopilotActive }" />
        <span>{{ autopilotActive ? 'Autopilot activo' : 'Autopilot apagado' }}</span>
      </div>
      <!-- Active: show deactivate -->
      <template v-if="autopilotActive">
        <button class="hw-dd-item hw-ap-danger" @click="deactivateAutopilot">
          Desactivar
        </button>
      </template>
      <!-- Inactive: show 3 activation modes -->
      <template v-else>
        <button class="hw-dd-item" @click="activatePassive">
          <span class="hw-ap-mode-label">Pasivo</span>
          <span class="hw-ap-hint">espera mensaje del cliente</span>
        </button>
        <button class="hw-dd-item" @click="activateProactive()">
          <span class="hw-ap-mode-label">Proactivo</span>
          <span class="hw-ap-hint">responde por contexto</span>
        </button>
        <button class="hw-dd-item" @click="showInstruction = !showInstruction">
          <span class="hw-ap-mode-label">Con instruccion</span>
        </button>
        <div v-if="showInstruction" class="hw-ap-instruction">
          <input
            v-model="instructionInput"
            type="text"
            placeholder="Ej: ofrecele el descuento del 10%"
            @keydown.enter="instructionInput.trim() && activateProactive(instructionInput.trim())"
          />
          <button
            class="hw-ap-send"
            :disabled="!instructionInput.trim()"
            @click="instructionInput.trim() && activateProactive(instructionInput.trim())"
          >
            Activar
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.hw-pill-wrap {
  position: relative;
}

.hw-pill {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 3px 8px;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 12px;
  background: transparent;
  color: rgb(var(--slate-10));
  font-size: 11px;
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s;
}
.hw-pill:hover {
  background: rgb(var(--slate-3));
  color: rgb(var(--slate-12));
}

.hw-chevron {
  width: 10px;
  height: 10px;
  flex-shrink: 0;
  opacity: 0.5;
}

/* Autopilot */
.hw-autopilot {
  border-color: rgb(var(--slate-5));
  color: rgb(var(--slate-10));
  font-weight: 600;
}
.hw-autopilot.active {
  border-color: rgba(0, 200, 83, 0.4);
  color: #00c853;
}

.hw-ap-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: rgb(var(--slate-8));
  flex-shrink: 0;
}
.hw-ap-dot.on {
  background: #00c853;
}

/* Dropdown */
.hw-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  background: rgb(var(--slate-2));
  border: 1px solid rgb(var(--slate-4));
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  min-width: 140px;
  z-index: 100;
  overflow: hidden;
}

.hw-ap-dropdown {
  right: 0;
  left: auto;
  min-width: 200px;
}

.hw-ap-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  font-size: 11px;
  font-weight: 600;
  color: rgb(var(--slate-11));
  border-bottom: 1px solid rgb(var(--slate-4));
}

.hw-dd-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: none;
  color: rgb(var(--slate-12));
  cursor: pointer;
  font-size: 12px;
  text-align: left;
}
.hw-dd-item:hover {
  background: rgb(var(--slate-3));
}

.hw-ap-danger {
  color: #ef4444 !important;
  font-weight: 600;
}
.hw-ap-danger:hover {
  background: rgba(239, 68, 68, 0.08) !important;
}

.hw-ap-mode-label {
  font-weight: 600;
  margin-right: 4px;
}

.hw-ap-hint {
  font-size: 10px;
  color: rgb(var(--slate-9));
  font-weight: 400;
}

.hw-ap-instruction {
  display: flex;
  gap: 4px;
  padding: 6px 8px 8px;
  border-top: 1px solid rgb(var(--slate-4));
}

.hw-ap-instruction input {
  flex: 1;
  padding: 4px 8px;
  border: 1px solid rgb(var(--slate-5));
  border-radius: 6px;
  background: rgb(var(--slate-1));
  color: rgb(var(--slate-12));
  font-size: 11px;
  outline: none;
}
.hw-ap-instruction input:focus {
  border-color: rgb(var(--blue-9));
}

.hw-ap-send {
  padding: 4px 10px;
  border: none;
  border-radius: 6px;
  background: #00c853;
  color: #fff;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.hw-ap-send:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
.hw-ap-send:not(:disabled):hover {
  background: #00b848;
}
</style>
